import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Settings2 } from "lucide-react";

const STORAGE_KEY = "ecpprofili_cookie_consent_v1";

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeConsent(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...value, ts: Date.now() }));
  } catch {}
  // Notify PostHog if available
  try {
    if (window.posthog && typeof window.posthog.opt_in_capturing === "function") {
      if (value.analytics) window.posthog.opt_in_capturing();
      else window.posthog.opt_out_capturing();
    }
  } catch {}
}

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) setOpen(true);
    else setAnalytics(!!existing.analytics);
  }, []);

  // Listen to external trigger from footer
  useEffect(() => {
    const handler = () => { setOpen(true); setShowPrefs(true); };
    window.addEventListener("ecp:open-cookie-preferences", handler);
    return () => window.removeEventListener("ecp:open-cookie-preferences", handler);
  }, []);

  const acceptAll = () => {
    writeConsent({ necessary: true, analytics: true });
    setOpen(false);
  };
  const rejectAll = () => {
    writeConsent({ necessary: true, analytics: false });
    setOpen(false);
  };
  const savePrefs = () => {
    writeConsent({ necessary: true, analytics });
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-3 lg:inset-x-auto lg:left-6 lg:right-auto lg:bottom-6 lg:max-w-md z-[60] animate-fade-up"
      role="dialog"
      aria-live="polite"
      aria-label="Consenso cookie"
      data-testid="cookie-banner"
    >
      <div className="rounded-2xl bg-white border border-brand-line shadow-2xl p-5 lg:p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-navy text-white flex items-center justify-center shrink-0">
            <Cookie className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-extrabold text-brand-navy text-lg">Rispettiamo la tua privacy</div>
            <p className="text-sm text-brand-muted mt-1 leading-relaxed">
              Usiamo cookie tecnici essenziali e, con il tuo consenso, cookie analitici per migliorare il sito.
              Leggi la nostra <Link to="/cookie-policy" className="text-brand-emerald font-semibold underline">Cookie Policy</Link>.
            </p>
          </div>
          <button
            onClick={rejectAll}
            className="text-brand-muted hover:text-brand-navy"
            aria-label="Chiudi"
            data-testid="cookie-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {showPrefs && (
          <div className="mt-5 space-y-3 border-t border-brand-line pt-4" data-testid="cookie-prefs">
            <Row label="Cookie tecnici" desc="Necessari al funzionamento del sito." disabled checked />
            <Row
              label="Cookie analitici"
              desc="Statistiche anonime per migliorare il sito (PostHog)."
              checked={analytics}
              onChange={(v) => setAnalytics(v)}
              testid="cookie-analytics-toggle"
            />
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          {!showPrefs && (
            <button
              onClick={() => setShowPrefs(true)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-navy hover:text-brand-emerald"
              data-testid="cookie-prefs-open"
            >
              <Settings2 className="w-4 h-4" /> Preferenze
            </button>
          )}
          <button
            onClick={rejectAll}
            className="text-sm font-semibold text-brand-navy px-4 py-2 rounded-full border border-brand-line hover:bg-brand-surface"
            data-testid="cookie-reject"
          >
            Solo essenziali
          </button>
          {showPrefs ? (
            <button onClick={savePrefs} className="btn-primary text-sm" data-testid="cookie-save">Salva preferenze</button>
          ) : (
            <button onClick={acceptAll} className="btn-primary text-sm" data-testid="cookie-accept">Accetta tutti</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, desc, checked, onChange, disabled, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
        className="mt-1 w-5 h-5 rounded accent-brand-emerald"
        data-testid={testid}
      />
      <div className="flex-1">
        <div className="font-semibold text-brand-navy text-sm">{label}{disabled && <span className="ml-2 text-xs text-brand-muted">(obbligatori)</span>}</div>
        <div className="text-xs text-brand-muted leading-relaxed">{desc}</div>
      </div>
    </label>
  );
}
